import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, formatApiError } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { AdminSidebar } from "@/pages/AdminUsers";
import {
  ArrowLeft, FileText, Signature, CheckCircle2, Circle, Plus, Trash2, Mail, ExternalLink,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import SignaturePad from "@/components/SignaturePad";

const STAGES = [
  { key: "agreement_sent",   label: "Agreement Sent" },
  { key: "agreement_signed", label: "Agreement Signed" },
  { key: "agent_setup",      label: "Agent Set Up" },
  { key: "agent_training",   label: "Agent Training" },
  { key: "agent_onboarded",  label: "Agent Onboarded" },
];

export default function AdminAgentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countersignOpen, setCountersignOpen] = useState(false);
  const [csForm, setCsForm] = useState({ signer_name: "", signer_title: "" });
  const [csSig, setCsSig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newItem, setNewItem] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: d }, { data: u }] = await Promise.all([
        api.get(`/admin/agents/${id}`),
        api.get("/admin/users").catch(() => ({ data: { users: [] } })),
      ]);
      setDoc(d);
      setUsers(u?.users || u || []);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const moveStage = async (stage) => {
    try {
      await api.patch(`/admin/agents/${id}/stage`, { stage });
      toast.success(`Moved to ${STAGES.find((s) => s.key === stage)?.label}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to move");
    }
  };

  const assignStage = async (stage, userId) => {
    try {
      await api.patch(`/admin/agents/${id}/stage`, {
        stage_assignments: { [stage]: userId || null },
      });
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || "Assign failed");
    }
  };

  const addItem = async (stage) => {
    const label = (newItem[stage] || "").trim();
    if (!label) return;
    try {
      await api.post(`/admin/agents/${id}/checklist/${stage}`, { label });
      setNewItem({ ...newItem, [stage]: "" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || "Add failed");
    }
  };

  const toggleItem = async (stage, item) => {
    try {
      await api.patch(`/admin/agents/${id}/checklist/${stage}/${item.id}`, { done: !item.done });
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || "Update failed");
    }
  };

  const removeItem = async (stage, item) => {
    if (!window.confirm(`Remove "${item.label}"?`)) return;
    try {
      await api.delete(`/admin/agents/${id}/checklist/${stage}/${item.id}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || "Remove failed");
    }
  };

  const countersign = async () => {
    if (!csSig) {
      toast.error("Please draw your signature");
      return;
    }
    if (!csForm.signer_name || !csForm.signer_title) {
      toast.error("Name and title required");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/admin/agents/${id}/countersign`, {
        signer_name: csForm.signer_name,
        signer_title: csForm.signer_title,
        signature_data_url: csSig,
      });
      toast.success("Agreement executed. Welcome email sent.");
      setCountersignOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || "Countersign failed");
    } finally {
      setSaving(false);
    }
  };

  const resendInvite = async () => {
    try {
      await api.post(`/admin/agents/${id}/resend-invite`);
      toast.success("Invitation email resent");
    } catch (e) {
      toast.error(formatApiError(e) || "Resend failed");
    }
  };

  if (loading || !doc) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Navbar />
        <main className="max-w-[1400px] mx-auto px-6 py-10">
          <div className="text-sm text-[#6C757D]">Loading…</div>
        </main>
      </div>
    );
  }

  const stageIdx = STAGES.findIndex((s) => s.key === doc.stage);
  const signLink = `${window.location.origin}/agent/sign/${doc.sign_token}`;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-6 py-10">
        <button
          type="button"
          onClick={() => navigate("/admin/agents")}
          className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold text-[#6C757D] hover:text-[#0B132B] mb-6"
          data-testid="agent-detail-back"
        >
          <ArrowLeft size={14} /> All agents
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-10">
          <AdminSidebar />
          <div className="space-y-8">
            {/* Header */}
            <Card className="bg-white border border-[#E9ECEF] rounded-sm p-8">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
                    {doc.status === "executed" ? "Executed" : doc.status === "signed" ? "Ready to countersign" : "Awaiting signature"}
                  </div>
                  <h1 className="font-display text-3xl font-black text-[#0B132B] mt-2 tracking-tight" data-testid="agent-detail-agency">
                    {doc.invite_agency_name}
                  </h1>
                  <div className="mt-2 text-sm text-[#6C757D]">
                    {doc.invite_name} · <a href={`mailto:${doc.invite_email}`} className="text-[#0B132B] hover:underline">{doc.invite_email}</a>
                  </div>
                </div>
              <div className="flex items-center gap-2 items-end justify-end flex-wrap">
                  {doc.status === "executed" && (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/agents/${doc.id}/executed.pdf`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 h-10 px-4 rounded-sm border border-[#0B132B] text-[#0B132B] hover:bg-[#0B132B]/5 text-sm font-semibold"
                      data-testid="agent-detail-download-pdf"
                    >
                      <FileText size={14} /> Executed PDF
                    </a>
                  )}
                </div>
                <div className="flex flex-col gap-2 items-end">
                  {doc.status === "signed" && (
                    <Dialog open={countersignOpen} onOpenChange={setCountersignOpen}>
                      <DialogTrigger asChild>
                        <Button className="bg-[#0B132B] hover:bg-[#0B132B]/90 text-white h-10 px-5" data-testid="agent-detail-countersign-btn">
                          <Signature size={14} className="mr-2" />
                          Countersign & execute
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-lg">
                        <DialogHeader>
                          <DialogTitle>Countersign for Advantage Warranty Group</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label>Your name</Label>
                            <Input
                              value={csForm.signer_name}
                              onChange={(e) => setCsForm({ ...csForm, signer_name: e.target.value })}
                              data-testid="agent-detail-cs-name"
                            />
                          </div>
                          <div>
                            <Label>Your title</Label>
                            <Input
                              value={csForm.signer_title}
                              onChange={(e) => setCsForm({ ...csForm, signer_title: e.target.value })}
                              placeholder="e.g. Managing Director"
                              data-testid="agent-detail-cs-title"
                            />
                          </div>
                          <div>
                            <Label>Signature</Label>
                            <SignaturePad onChange={setCsSig} />
                          </div>
                          <div className="text-xs text-[#6C757D] leading-relaxed">
                            Executing this agreement will create the agency in our system,
                            issue a login for {doc.invite_email}, and send a welcome email
                            with a set-password link.
                          </div>
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setCountersignOpen(false)}>Cancel</Button>
                          <Button onClick={countersign} disabled={saving} className="bg-[#0B132B] hover:bg-[#0B132B]/90 text-white" data-testid="agent-detail-cs-submit">
                            {saving ? "Executing…" : "Execute agreement"}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                  {doc.status === "sent" && (
                    <>
                      <Button variant="outline" onClick={resendInvite} className="h-10" data-testid="agent-detail-resend">
                        <Mail size={14} className="mr-2" /> Resend invitation
                      </Button>
                      <a
                        href={signLink}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-[#6C757D] hover:text-[#0B132B] flex items-center gap-1"
                      >
                        <ExternalLink size={12} /> Preview sign page
                      </a>
                    </>
                  )}
                  {doc.status === "executed" && doc.created_agency_id && (
                    <a
                      href={`/admin/agencies/${doc.created_agency_id}/users`}
                      className="text-xs text-[#0B132B] hover:underline flex items-center gap-1"
                    >
                      <ExternalLink size={12} /> View agency
                    </a>
                  )}
                </div>
              </div>

              {/* Progress bar */}
              <div className="mt-8">
                <div className="flex items-center gap-1">
                  {STAGES.map((s, i) => (
                    <React.Fragment key={s.key}>
                      <div className={`flex-1 h-1.5 rounded-full ${i <= stageIdx ? "bg-[#D4AF37]" : "bg-[#E9ECEF]"}`} />
                    </React.Fragment>
                  ))}
                </div>
                <div className="mt-2 flex justify-between text-[9px] font-mono uppercase tracking-[0.14em] text-[#6C757D]">
                  {STAGES.map((s) => (
                    <div key={s.key} className={s.key === doc.stage ? "text-[#0B132B] font-bold" : ""}>{s.label}</div>
                  ))}
                </div>
              </div>
            </Card>

            {/* Signed details */}
            {doc.status !== "sent" && (
              <Card className="bg-white border border-[#E9ECEF] rounded-sm p-8" data-testid="agent-detail-signed-block">
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B] mb-4 flex items-center gap-2">
                  <FileText size={14} className="text-[#D4AF37]" /> Signed agreement
                </div>
                <div className="grid grid-cols-2 gap-6 text-sm">
                  <Kv label="Effective date" value={doc.effective_date} />
                  <Kv label="Legal name" value={doc.legal_name} />
                  <Kv label="Signatory" value={`${doc.signatory_name} (${doc.signatory_title})`} />
                  <Kv label="Signed" value={doc.signed_at ? new Date(doc.signed_at).toLocaleString() : "—"} />
                  <Kv label="Notice address" value={doc.notice_address} colSpan={2} />
                </div>
                {doc.agent_signature_data_url && (
                  <div className="mt-6">
                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6C757D] mb-2">Agent signature</div>
                    <img src={doc.agent_signature_data_url} alt="Agent signature" className="max-h-24 border border-[#E9ECEF] rounded-sm p-2 bg-white" />
                  </div>
                )}
                {doc.status === "executed" && doc.admin_signature_data_url && (
                  <div className="mt-6">
                    <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6C757D] mb-2">
                      Countersigned by {doc.admin_signer_name} ({doc.admin_signer_title})
                    </div>
                    <img src={doc.admin_signature_data_url} alt="Admin signature" className="max-h-24 border border-[#E9ECEF] rounded-sm p-2 bg-white" />
                  </div>
                )}
              </Card>
            )}

            {/* Stages + checklists */}
            <Card className="bg-white border border-[#E9ECEF] rounded-sm p-8">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0B132B] mb-6">
                Onboarding pipeline
              </div>
              <div className="space-y-6">
                {STAGES.map((s) => {
                  const items = (doc.checklist || {})[s.key] || [];
                  const assigned = (doc.stage_assignments || {})[s.key] || null;
                  const isCurrent = s.key === doc.stage;
                  return (
                    <div key={s.key} className={`border rounded-sm p-5 ${isCurrent ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-[#E9ECEF]"}`} data-testid={`agent-stage-${s.key}`}>
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">{s.label}</div>
                          {isCurrent && (
                            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#D4AF37] mt-1">
                              Current stage
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Select
                            value={assigned || "__none__"}
                            onValueChange={(v) => assignStage(s.key, v === "__none__" ? null : v)}
                          >
                            <SelectTrigger className="h-8 w-56 text-xs rounded-sm" data-testid={`agent-stage-assign-${s.key}`}>
                              <SelectValue placeholder="Unassigned" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">Unassigned</SelectItem>
                              {users.map((u) => (
                                <SelectItem key={u.id} value={u.id}>
                                  {u.name || u.email}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {!isCurrent && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => moveStage(s.key)}
                              className="h-8 text-xs"
                              data-testid={`agent-stage-move-${s.key}`}
                            >
                              Move here
                            </Button>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        {items.map((it) => (
                          <div key={it.id} className="flex items-center gap-3 text-sm">
                            <button
                              type="button"
                              onClick={() => toggleItem(s.key, it)}
                              className="text-[#D4AF37] hover:text-[#0B132B]"
                              data-testid={`agent-checklist-toggle-${it.id}`}
                            >
                              {it.done ? <CheckCircle2 size={18} /> : <Circle size={18} className="text-[#6C757D]" />}
                            </button>
                            <div className={`flex-1 ${it.done ? "line-through text-[#6C757D]" : "text-[#0B132B]"}`}>
                              {it.label}
                              {it.done && it.done_by && (
                                <span className="ml-2 text-[10px] text-[#6C757D]">by {it.done_by}</span>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => removeItem(s.key, it)}
                              className="text-[#6C757D] hover:text-red-600"
                              data-testid={`agent-checklist-remove-${it.id}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                        <div className="flex items-center gap-2 pt-2">
                          <Input
                            placeholder="Add checklist item…"
                            value={newItem[s.key] || ""}
                            onChange={(e) => setNewItem({ ...newItem, [s.key]: e.target.value })}
                            onKeyDown={(e) => e.key === "Enter" && addItem(s.key)}
                            className="h-9 text-sm"
                            data-testid={`agent-checklist-input-${s.key}`}
                          />
                          <Button
                            size="sm"
                            onClick={() => addItem(s.key)}
                            className="h-9 bg-[#0B132B] text-white"
                            data-testid={`agent-checklist-add-${s.key}`}
                          >
                            <Plus size={14} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}

function Kv({ label, value, colSpan = 1 }) {
  return (
    <div className={colSpan === 2 ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6C757D]">{label}</div>
      <div className="mt-1 text-sm text-[#0B132B]">{value || "—"}</div>
    </div>
  );
}
